import React, { useContext, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { useController, useFormContext } from 'react-hook-form';
import { PapiDeviation } from '@wix/da-papi-types';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import SelectField from '@wix/da-ds/pkg/Dropdown/select/SelectField';
import BooleanInput from '@wix/da-ds/pkg/formControls/dsPresets/BooleanInput';
import InputLabelText from '@wix/da-ds/pkg/formControls/lego/InputLabelText';
import FormElement from '@wix/da-ds/pkg/formControls/layouts/FormElement';
import { getDeviationType } from '@wix/da-shared-react/pkg/DeviationViews/_helpers';
import {
  DeviationOriginalFile,
  getDeviationOriginalFile,
} from '@wix/da-shared-react/pkg/utils/deviations';
import { DeviationTypes } from '@wix/da-shared-react/pkg/types/deviation';
import MediaService from '@wix/da-shared-react/pkg/utils/DeviationMediaService';
import { OriginalDeviationFieldName } from '../../types';
import { MIN_SIZE_FOR_WATERMARK } from '../../../../fields/AddWatermarkField/AddWatermarkField';

import s from './DisplayOptionsField.scss';

interface Props {
  deviation?: PapiDeviation;
}

const DisplayOptionsField: React.FC<Props> = ({ deviation }) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const id = useId();
  const { control } = useFormContext();
  const { field: displayResolutionField } = useController({
    name: OriginalDeviationFieldName.DISPLAY_RESOLUTION,
    control,
  });

  const { field: watermarkField } = useController({
    name: OriginalDeviationFieldName.ADD_WATERMARK,
    control,
  });

  // Lifted from the standard submit form's DisplayOptionsForm:
  // Only show display options if the fullview is eligible
  const isFilm = getDeviationType(deviation) === DeviationTypes.FILM;
  const isGif = !!deviation && MediaService.isGif(deviation);
  const originalFile: DeviationOriginalFile =
    getDeviationOriginalFile(deviation);
  const { width: fileWidth = 0, height: fileHeight = 0 } = originalFile ?? {};
  const isTooSmallForDisplayOpts =
    fileWidth < MIN_SIZE_FOR_WATERMARK && fileHeight < MIN_SIZE_FOR_WATERMARK;

  if (isFilm || isGif || isTooSmallForDisplayOpts) {
    return null;
  }

  const options = [
    { value: '0', width: fileWidth, height: fileHeight },
    { value: '1', width: 400 },
    { value: '2', width: 600 },
    { value: '3', width: 800 },
    { value: '4', width: 900 },
    { value: '5', width: 1024 },
    { value: '6', width: 1280 },
    { value: '7', width: 1600 },
    { value: '8', width: 1920 },
  ];

  return (
    <FormElement
      inputId={id}
      className={s['root']}
      label={
        <InputLabelText>
          {t('originalDeviationPopup.form.displayResolution.label')}
        </InputLabelText>
      }
    >
      <div className={s['inputs']}>
        <SelectField
          id={id}
          className={s['display-resolution']}
          label={t('originalDeviationPopup.form.displayResolution.label')}
          items={
            options
              .filter(item => item.width <= fileWidth)
              .map(({ value, width, height }) => ({
                value,
                label: t(
                  `deviationSubmit.form.displayResolution.option.label.${
                    value === '0' ? 'original' : 'preset'
                  }`,
                  {
                    width,
                    height,
                  }
                )
              }))
          }
          value={displayResolutionField.value}
          onSelect={displayResolutionField.onChange}
          withBlankOption={false}
          isLabelHidden
        />
        <BooleanInput
          id={id}
          label={t('deviationSubmit.form.addWatermark.label')}
          onChange={watermarkField.onChange}
          value={watermarkField.value}
          size={isMobile ? 'large' : 'small'}
          noBottomSpacing
        />
      </div>
    </FormElement>
  );
};
DisplayOptionsField.displayName = 'DisplayOptionsField';

export default DisplayOptionsField;
